/**
 * @module linkage/shared/mergeConfigPatch
 * @description MERGE_QB_PATCH 写入 ChartSettings Context 时的合并规则
 *
 * field_setting / description（`IDescriptionSetting`）等是「一层 key + 多层 sub-key」结构。
 * 自定义字段改名时 migrate 会删除旧 sub-key（如 t0_e），patch 里用 `t0_e: undefined` 表示删除；
 * 若只做 `{ ...old, ...patch }` 浅合并，旧 sub-key 永远不会被删掉，所以 DEEP_MERGE_KEYS 要单独处理 undefined。
 */

/** Record 型配置：QB patch 与已有 sub-key 浅合并，sub-key 为 undefined 时表示删除 */
export const DEEP_MERGE_KEYS = new Set<string>([
  'description',
  'field_setting',
  'statistics',
]);

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** 将 QB 联动 patch 合并进 target（base 或 draft 的一层） */
export function mergeConfigLayer(
  target: Record<string, unknown>,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...target };
  for (const [key, value] of Object.entries(patch)) {
    if (
      DEEP_MERGE_KEYS.has(key) &&
      isPlainRecord(next[key]) &&
      isPlainRecord(value)
    ) {
      const merged = {
        ...(next[key] as Record<string, unknown>),
      };
      for (const [subKey, subValue] of Object.entries(value)) {
        // 改名迁移：buildRecordSubKeyPatch 对移除的 sub-key 置 undefined
        if (subValue === undefined) {
          delete merged[subKey];
        } else {
          merged[subKey] = subValue;
        }
      }
      next[key] = merged;
    } else if (value === undefined) {
      delete next[key];
    } else {
      next[key] = value;
    }
  }
  return next;
}
